import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { apiGet, apiPut } from '@/api/client';
import type { User } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { Field, FormActions, Input } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useIsSelf } from '@/features/users/selfIdentity';
import { PreferredLanguageSelect } from '@/i18n/PreferredLanguageSelect';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * Editing one person's record. Two rules from the endpoint shape this form:
 *
 * - An omitted field is written as `null` rather than left alone, so the
 *   editable non-private fields are *always* sent, whatever the caller typed.
 * - The private fields are the exception: sending one with a value for
 *   somebody else is refused with a 403, and omitting them leaves them
 *   untouched. So they are offered — and sent — only for the caller's own
 *   record.
 *
 * `name` and `email` are not here: they are synced from Keycloak and ignored
 * on write. An address is changed through its own endpoint, on the detail page.
 */
export function UserEditPage() {
  const { t } = useTranslation();
  const { userId = '' } = useParams();
  const user = useAsync(() => apiGet('/users/{userId}', { params: { userId } }), [userId]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('users.edit.title')} />
      <AsyncSection state={user} label={t('users.detail.thisPerson')}>
        {(data) => <UserEditForm userId={userId} user={data} />}
      </AsyncSection>
    </section>
  );
}

function UserEditForm({ userId, user }: { userId: string; user: User }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSelf = useIsSelf(userId);
  const backTo = `/users/${userId}`;

  const [nickname, setNickname] = useState(user.nickname ?? '');
  const [language, setLanguage] = useState(user.preferred_language ?? '');
  const [region, setRegion] = useState(user.region ?? '');
  const [gender, setGender] = useState(user.gender != null ? String(user.gender) : '');
  const [birthdate, setBirthdate] = useState(user.birthdate ?? '');
  const [height, setHeight] = useState(user.height != null ? String(user.height) : '');
  const [weight, setWeight] = useState(user.weight != null ? String(user.weight) : '');

  const blank = (value: string) => value.trim() === '';

  const form = useSubmit(async () => {
    await apiPut('/users/{userId}', {
      params: { userId },
      body: {
        nickname: blank(nickname) ? null : nickname,
        preferred_language: blank(language) ? null : language,
        region: blank(region) ? null : region,
        ...(isSelf
          ? {
              gender: blank(gender) ? null : Number(gender),
              birthdate: blank(birthdate) ? null : birthdate,
              height: blank(height) ? null : Number(height),
              weight: blank(weight) ? null : Number(weight),
            }
          : {}),
      },
    });
    await navigate(backTo);
  });

  return (
    <Card className="max-w-2xl">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <Alert tone="info">{t('users.edit.lead')}</Alert>
        <Field label={t('users.detail.nickname')}>
          <Input value={nickname} onChange={(e) => setNickname(e.target.value)} />
        </Field>
        <Field label={t('profile.preferredLanguage')}>
          <PreferredLanguageSelect value={language} onChange={setLanguage} />
        </Field>
        <Field label={t('users.detail.region')}>
          <Input
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="BE"
            maxLength={2}
          />
        </Field>

        {isSelf && (
          <>
            <Field label={t('users.detail.gender')}>
              <Input type="number" value={gender} onChange={(e) => setGender(e.target.value)} />
            </Field>
            <Field label={t('users.detail.birthdate')}>
              <Input type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
            </Field>
            <Field label={t('users.detail.height')}>
              <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} />
            </Field>
            <Field label={t('users.detail.weight')}>
              <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} />
            </Field>
          </>
        )}

        <SubmitError error={form.error} />
        <FormActions>
          <Button as={Link} to={backTo} variant="ghost">
            {t('users.edit.cancel')}
          </Button>
          <Button type="submit" disabled={form.pending}>
            {t('users.edit.save')}
          </Button>
        </FormActions>
      </form>
    </Card>
  );
}
